import type { Vehicle } from "./useVehicleStream";

// Per-vehicle position history used by the Stringline diagram.
//
// The hook keeps a rolling buffer of the most recent ~30 minutes of vehicle
// snapshots in a ref (cheap to mutate) and exposes a monotonically-bumped
// `version` counter so consumers can re-render at the cadence of the SSE
// stream rather than on every parent render.

export type Sample = {
  ts: number;
  lat: number;
  lon: number;
  bearing?: number;
  routeId: string;
  routeColor?: string;
  routeShortName?: string;
  tripId?: string;
  tripHeadsign?: string;
};

export type VehicleHistory = {
  vehicleId: string;
  routeId: string;
  routeColor?: string;
  routeShortName?: string;
  samples: Sample[];
};

export type HistoryStore = {
  /** vehicleId -> ordered samples (oldest first). */
  byVehicle: Map<string, VehicleHistory>;
};

export function createHistoryStore(): HistoryStore {
  return { byVehicle: new Map() };
}

// ingest folds a fresh snapshot into the store, dropping samples older than
// `windowSec` and de-duping consecutive identical timestamps. Returns true
// if anything actually changed (i.e. a new sample was appended), so the
// caller can avoid re-rendering when the snapshot's `ts` hasn't advanced.
export function ingestSnapshot(
  store: HistoryStore,
  vehicles: Vehicle[],
  nowSec: number,
  windowSec: number,
): boolean {
  let changed = false;
  const cutoff = nowSec - windowSec;

  for (const v of vehicles) {
    if (!v.vehicle_id || !v.route_id) continue;
    if (typeof v.lat !== "number" || typeof v.lon !== "number") continue;
    const ts = v.ts || nowSec;

    let entry = store.byVehicle.get(v.vehicle_id);
    if (!entry) {
      entry = {
        vehicleId: v.vehicle_id,
        routeId: v.route_id,
        routeColor: v.route_color,
        routeShortName: v.route_short_name,
        samples: [],
      };
      store.byVehicle.set(v.vehicle_id, entry);
    }
    // Keep route metadata fresh in case the vehicle gets reassigned.
    entry.routeId = v.route_id;
    entry.routeColor = v.route_color ?? entry.routeColor;
    entry.routeShortName = v.route_short_name ?? entry.routeShortName;

    const last = entry.samples[entry.samples.length - 1];
    if (last && last.ts === ts && last.lat === v.lat && last.lon === v.lon) {
      continue;
    }
    entry.samples.push({
      ts,
      lat: v.lat,
      lon: v.lon,
      bearing: v.bearing,
      routeId: v.route_id,
      routeColor: v.route_color,
      routeShortName: v.route_short_name,
      tripId: v.trip_id,
      tripHeadsign: v.trip_headsign,
    });
    changed = true;
  }

  // Trim each vehicle's history to the time window, and drop vehicles that
  // haven't reported anything inside the window.
  for (const [id, entry] of store.byVehicle) {
    while (entry.samples.length > 0 && entry.samples[0].ts < cutoff) {
      entry.samples.shift();
      changed = true;
    }
    if (entry.samples.length === 0) {
      store.byVehicle.delete(id);
      changed = true;
    }
  }

  return changed;
}

// seedHistory folds a batch of pre-existing per-vehicle samples (e.g. the
// response from /api/realtime/vehicles/history) into the store, merging with
// any samples already present. Used on app startup so the live Stringline
// diagram has a populated trail immediately instead of waiting for ~30 min
// of SSE snapshots to accumulate.
//
// Samples are merged by timestamp (last-write-wins per vehicle/ts), then
// re-sorted ascending and trimmed to `windowSec` ending at `nowSec`.
export function seedHistory(
  store: HistoryStore,
  seed: VehicleHistory[],
  nowSec: number,
  windowSec: number,
): boolean {
  let changed = false;
  const cutoff = nowSec - windowSec;

  for (const v of seed) {
    if (!v.vehicleId || !v.routeId) continue;
    let entry = store.byVehicle.get(v.vehicleId);
    if (!entry) {
      entry = {
        vehicleId: v.vehicleId,
        routeId: v.routeId,
        routeColor: v.routeColor,
        routeShortName: v.routeShortName,
        samples: [],
      };
      store.byVehicle.set(v.vehicleId, entry);
    }
    entry.routeId = v.routeId || entry.routeId;
    entry.routeColor = v.routeColor ?? entry.routeColor;
    entry.routeShortName = v.routeShortName ?? entry.routeShortName;

    const byTs = new Map<number, Sample>();
    for (const s of entry.samples) byTs.set(s.ts, s);
    for (const s of v.samples) {
      if (s.ts < cutoff) continue;
      if (!byTs.has(s.ts)) {
        byTs.set(s.ts, s);
        changed = true;
      }
    }
    const merged = Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
    entry.samples = merged;
  }

  for (const [id, entry] of store.byVehicle) {
    while (entry.samples.length > 0 && entry.samples[0].ts < cutoff) {
      entry.samples.shift();
      changed = true;
    }
    if (entry.samples.length === 0) {
      store.byVehicle.delete(id);
      changed = true;
    }
  }

  return changed;
}
